import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Separator } from '../components/ui/separator';
import { ArrowLeft, ChevronLeft, ChevronRight } from 'lucide-react';
import { FaWhatsapp } from 'react-icons/fa';
import { motion } from 'framer-motion';
import axios from 'axios';
import { RichText } from '../components/RichText';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function CarDetail() {
  const { id } = useParams();
  const { t } = useLanguage();
  const [car, setCar] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeImage, setActiveImage] = useState(0);

  useEffect(() => {
    const fetchCar = async () => {
      try {
        const res = await axios.get(`${API}/cars/${id}`);
        setCar(res.data);
      } catch (err) {
        console.error('Failed to fetch car:', err);
      }
      setLoading(false);
    };
    fetchCar();
  }, [id]);

  const formatPrice = (price) =>
    new Intl.NumberFormat('en-US', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(price);

  const formatMileage = (mileage) => new Intl.NumberFormat('en-US').format(mileage);

  if (loading) {
    return (
      <div className="min-h-screen pt-28 flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-[#ED2227] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!car) {
    return (
      <div className="min-h-screen pt-28 flex items-center justify-center">
        <p className="text-zinc-500">Vehicle not found</p>
      </div>
    );
  }

  const images = car.images?.length > 0 ? car.images : ['https://images.unsplash.com/photo-1503376780353-7e6692767b70?w=800'];

  const prevImage = () => setActiveImage((p) => (p > 0 ? p - 1 : images.length - 1));
  const nextImage = () => setActiveImage((p) => (p < images.length - 1 ? p + 1 : 0));

  const specs = [
    { label: t('inventory.year'), value: car.year },
    { label: t('inventory.mileage'), value: `${formatMileage(car.mileage)} km` },
    { label: t('inventory.condition'), value: car.condition },
    { label: t('carDetail.engine'), value: car.engine },
    { label: t('carDetail.transmission'), value: car.transmission },
    { label: t('carDetail.fuelType'), value: car.fuel_type },
    { label: t('carDetail.color'), value: car.color },
    { label: t('inventory.stockNumber'), value: car.stock_number },
  ].filter((s) => s.value);

  return (
    <div className="min-h-screen pt-24 md:pt-28 pb-24" data-testid="car-detail-page">
      <div className="max-w-[1400px] mx-auto px-6 md:px-12">
        {/* Back */}
        <Link
          to="/inventory"
          className="inline-flex items-center gap-2 text-sm text-zinc-500 hover:text-white mb-8 transition-colors uppercase tracking-widest"
          data-testid="back-to-inventory-link"
        >
          <ArrowLeft className="w-4 h-4" strokeWidth={1.5} />
          {t('carDetail.backToInventory')}
        </Link>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 md:gap-12">
          {/* Image Gallery */}
          <div className="lg:col-span-7">
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="relative aspect-[4/3] overflow-hidden rounded-md bg-[#121212]"
            >
              <img
                src={images[activeImage]}
                alt={`${car.make} ${car.model}`}
                className="w-full h-full object-cover"
                data-testid="car-main-image"
              />

              {images.length > 1 && (
                <>
                  <button
                    onClick={prevImage}
                    className="absolute left-4 top-1/2 -translate-y-1/2 w-10 h-10 bg-black/50 backdrop-blur-sm rounded-full flex items-center justify-center text-white hover:bg-black/80 transition-colors"
                    data-testid="prev-image-btn"
                  >
                    <ChevronLeft className="w-5 h-5" />
                  </button>
                  <button
                    onClick={nextImage}
                    className="absolute right-4 top-1/2 -translate-y-1/2 w-10 h-10 bg-black/50 backdrop-blur-sm rounded-full flex items-center justify-center text-white hover:bg-black/80 transition-colors"
                    data-testid="next-image-btn"
                  >
                    <ChevronRight className="w-5 h-5" />
                  </button>
                </>
              )}

              {/* Badges */}
              <div className="absolute top-4 left-4 flex gap-2">
                {car.featured && (
                  <Badge className="bg-[#ED2227] text-white text-[10px] tracking-widest uppercase border-0 rounded-sm">
                    {t('inventory.featured')}
                  </Badge>
                )}
                <Badge className="bg-black/60 backdrop-blur-sm text-white text-[10px] tracking-widest uppercase border-0 rounded-sm">
                  {t('carDetail.exportReady')}
                </Badge>
              </div>
            </motion.div>

            {/* Thumbnails */}
            {images.length > 1 && (
              <div className="flex gap-3 mt-4 overflow-x-auto pb-2">
                {images.map((img, i) => (
                  <button
                    key={i}
                    onClick={() => setActiveImage(i)}
                    className={`shrink-0 w-20 h-16 rounded-sm overflow-hidden border-2 transition-colors ${
                      i === activeImage ? 'border-[#ED2227]' : 'border-transparent opacity-50 hover:opacity-100'
                    }`}
                    data-testid={`thumbnail-${i}`}
                  >
                    <img src={img} alt="" className="w-full h-full object-cover" />
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Details */}
          <div className="lg:col-span-5">
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.2 }}
              className="space-y-6"
            >
              <div>
                <span className="text-xs font-mono uppercase tracking-[0.2em] text-zinc-600 block mb-2">
                  {car.stock_number}
                </span>
                <h1 className="text-3xl md:text-4xl font-black tracking-tight text-white font-[Chivo]">
                  {car.make} {car.model}
                </h1>
              </div>

              <div className="text-3xl md:text-4xl font-black text-[#ED2227] font-[Chivo]">
                {formatPrice(car.price)}
              </div>

              <Separator className="bg-zinc-800/50" />

              {/* Specs Grid */}
              <div>
                <h3 className="text-xs font-mono uppercase tracking-[0.2em] text-zinc-500 mb-4">
                  {t('carDetail.specifications')}
                </h3>
                <div className="grid grid-cols-2 gap-4">
                  {specs.map((spec, i) => (
                    <div key={i} className="bg-[#121212] border border-zinc-800/30 rounded-sm p-3">
                      <span className="text-[10px] font-mono uppercase tracking-widest text-zinc-600 block mb-1">
                        {spec.label}
                      </span>
                      <span className="text-sm text-white font-medium">{spec.value}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Description */}
              {car.description && (
                <div>
                  <h3 className="text-xs font-mono uppercase tracking-[0.2em] text-zinc-500 mb-3">
                    {t('carDetail.description')}
                  </h3>
                  <RichText html={car.description} testId="car-description" />
                </div>
              )}

              <Separator className="bg-zinc-800/50" />

              {/* CTA */}
              <div className="space-y-3">
                <Link to={`/contact?stock=${car.stock_number}&vehicle=${encodeURIComponent(car.make + ' ' + car.model)}`} className="block">
                  <Button
                    className="w-full bg-[#ED2227] text-white hover:bg-red-700 rounded-sm font-bold uppercase tracking-wider h-12 text-sm"
                    data-testid="inquire-button"
                  >
                    {t('carDetail.inquire')}
                  </Button>
                </Link>
                {(() => {
                  const vehicleLabel = [car.year, car.make, car.model].filter(Boolean).join(' ');
                  const waMessage = t('carDetail.whatsappTemplate')
                    .replace('{vehicle}', vehicleLabel)
                    .replace('{stock}', car.stock_number || '-')
                    .replace('{price}', formatPrice(car.price));
                  const waHref = `https://wa.me/31615254599?text=${encodeURIComponent(waMessage)}`;
                  return (
                    <a
                      href={waHref}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="block"
                    >
                      <Button
                        variant="outline"
                        className="w-full border-[#25D366]/30 text-[#25D366] hover:bg-[#25D366] hover:text-white rounded-sm font-bold uppercase tracking-wider h-12 text-sm gap-2"
                        data-testid="whatsapp-inquire-button"
                      >
                        <FaWhatsapp className="w-5 h-5" />
                        {t('carDetail.whatsapp')}
                      </Button>
                    </a>
                  );
                })()}
              </div>
            </motion.div>
          </div>
        </div>
      </div>
    </div>
  );
}
